<template>
  <div
    class="race-analyzer-v2"
    :class="layoutClass"
  >
    <!-- Long operations: manual corrections, batch apply, full analysis -->
    <transition name="ra-operation-fade">
      <div
        v-if="operationBlockingUi"
        class="ra-operation-overlay"
        :class="layoutClass"
        role="status"
        aria-live="polite"
      >
        <div class="ra-operation-overlay__panel">
          <b-spinner
            class="ra-operation-overlay__spinner mb-3"
            variant="primary"
            style="width: 3rem; height: 3rem;"
          />
          <h5 class="ra-operation-overlay__title mb-2">
            {{ operationBlockingUi.title }}
          </h5>
          <p class="ra-operation-overlay__hint mb-0">
            {{ operationBlockingUi.hint }}
          </p>
        </div>
      </div>
    </transition>

    <!-- Loading overlay: only while distances are not loaded yet -->
    <div
      v-if="isLoading && availableDistances.length === 0"
      class="loading-overlay"
    >
      <b-spinner
        large
        variant="primary"
      />
      <p class="mt-3">
        Загрузка...
      </p>
    </div>

    <!-- Main content: show as soon as we have distances; tab content shows its own loading while state loads -->
    <div v-else>
      <!-- Task 10: finalized-state banner above the toolbar -->
      <b-alert
        v-if="isLifecycleFinalized"
        variant="success"
        show
        class="mb-3 lifecycle-finalized-banner"
      >
        <b-icon
          icon="lock-fill"
          class="mr-2"
        />
        <strong>Дистанция финализирована.</strong>
        Обычные правки заблокированы. Чтобы внести изменения, снимите финализацию во вкладке «Конфигурация» с указанием причины.
      </b-alert>

      <!-- Distance Selector -->
      <b-card class="mb-3 distance-selector-card">
        <b-row>
          <b-col md="6">
            <b-form-group
              label="Дистанция:"
              label-class="font-weight-bold"
            >
              <b-form-select
                v-model="selectedDistanceId"
                :options="distanceOptions"
                :disabled="availableDistances.length === 0"
              >
                <template #first>
                  <b-form-select-option
                    :value="null"
                    disabled
                  >
                    -- Выберите дистанцию --
                  </b-form-select-option>
                </template>
              </b-form-select>
              <small class="text-muted">
                Каждая дистанция имеет свою конфигурацию и результаты
              </small>
            </b-form-group>
          </b-col>
          <b-col
            md="6"
            class="d-flex align-items-center"
          >
            <b-alert
              v-if="hasInvalidDistances"
              variant="danger"
              show
              class="mb-0 w-100"
            >
              <b-icon
                icon="exclamation-triangle"
                class="mr-2"
              />
              У {{ invalidDistancesCount }} участников указана несуществующая дистанция.
              Исправьте в разделе "Конфигурация" → "Участники" перед запуском анализа.
              <b-button
                variant="outline-danger"
                size="sm"
                class="ml-2"
                @click="openInvalidParticipantsInConfig"
              >
                Перейти к проблемным участникам
              </b-button>
              <b-button
                v-if="validDistancesCount > 0"
                variant="warning"
                size="sm"
                class="ml-3"
                :disabled="isAnalyzing || isRefreshing || isSavingConfig"
                @click="performAnalysisForValidOnly"
              >
                <b-spinner
                  v-if="isAnalyzing"
                  small
                  class="mr-1"
                />
                Анализировать только валидных ({{ validDistancesCount }})
              </b-button>
            </b-alert>
          </b-col>
        </b-row>
        <b-row class="mt-2">
          <b-col md="6">
            <b-form-group
              label="ID старта во внешней системе"
              label-class="font-weight-bold"
            >
              <b-form-input
                v-model="config.externalStartId"
                placeholder="Оставьте пустым, чтобы использовать текущий ID старта"
              />
              <small class="text-muted d-block mt-25">
                Используется для запросов во внешнюю систему. Не забудьте сохранить конфигурацию после изменения.
              </small>
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Mocks section: current mode + manage button (developers only) -->
        <b-row class="mt-3">
          <b-col cols="12">
            <div class="mocks-section p-3 border rounded">
              <div class="d-flex align-items-center mb-2">
                <h6 class="mb-0 font-weight-bold">
                  Моки (внешний API)
                </h6>
                <b-badge
                  variant="secondary"
                  class="ml-2"
                >
                  Только для разработчиков
                </b-badge>
              </div>
              <div class="d-flex align-items-center flex-wrap">
                <span class="mr-3">
                  <span class="text-muted">Режим:</span>
                  <b-badge
                    v-if="mockMode === true"
                    variant="info"
                    class="ml-1"
                  >Мок из БД</b-badge>
                  <b-badge
                    v-else-if="mockMode === false"
                    variant="secondary"
                    class="ml-1"
                  >Реальный API (bc70a.ru)</b-badge>
                  <span
                    v-else
                    class="text-muted ml-1"
                  >—</span>
                </span>
                <b-button
                  variant="outline-secondary"
                  size="sm"
                  @click="openMockManagement"
                >
                  Управление моками
                </b-button>
              </div>
            </div>
          </b-col>
        </b-row>
      </b-card>

      <!-- Sticky toolbar: live monitor + main tabs (same band as former monitor-only sticky) -->
      <div
        class="race-analyzer-v2__sticky-toolbar"
        :class="layoutClass"
      >
        <live-monitor
          :is-enabled="!isPaused"
          :interval="refreshIntervalSec"
          :is-refreshing="isRefreshing"
          :countdown="countdown"
          :pending-changes="pendingManipulations"
          :has-unsaved-config="hasUnsavedConfigChanges"
          :is-saving-config="isSavingConfig"
          :is-applying-changes="isApplyingChanges"
          :is-analyzing="isAnalyzing"
          :blocking-label="operationBlockingShortLabel"
          @toggle="toggleAutoRefresh"
          @interval-change="handleRefreshIntervalChange"
          @refresh-now="manualRefresh"
          @apply-changes="applyBatchChanges"
          @discard-changes="discardChanges"
          @discard-config="discardConfigChanges"
          @save-config="saveConfiguration"
        />

        <div
          v-if="isLoading"
          class="race-analyzer-v2__sticky-tabstrip card ra-tabstrip-merge-bottom"
        >
          <div class="card-body py-4 text-center">
            <b-spinner variant="primary" />
            <p class="mt-2 mb-0 small text-muted">
              Загрузка состояния дистанции…
            </p>
          </div>
        </div>

        <div
          v-else
          class="race-analyzer-v2__sticky-tabstrip card ra-tabstrip-merge-bottom"
        >
          <div class="card-header border-bottom-0 pb-0 pt-2 px-2 bg-transparent">
            <ul
              class="nav nav-tabs card-header-tabs mb-0"
              role="tablist"
            >
              <li
                v-for="(tab, i) in mainAnalyzerTabs"
                :key="tab.key"
                class="nav-item"
                role="presentation"
              >
                <button
                  type="button"
                  class="nav-link"
                  :class="{ active: activeTab === i }"
                  role="tab"
                  :aria-selected="activeTab === i ? 'true' : 'false'"
                  @click="activeTab = i"
                >
                  {{ tab.title }}
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <b-tabs
        v-if="!isLoading"
        v-model="activeTab"
        class="race-analyzer-v2__main-tabs"
        card
        nav-class="d-none"
      >
        <!-- Results Tab -->
        <b-tab title="📋 Результаты">
          <results-filters
            :results="results"
            :initial-filters="currentFilters"
            :group-options-from-meta="filterMetaGroups"
            :hide-gun-chip-toggle="chipTimeDualResultColumns"
            :official-time-mode="config.officialTimeMode || 'gunTime'"
            @update="handleFiltersUpdate"
          />

          <results-import-panel
            :start-id="startId"
            :distance-id="selectedDistanceId"
            :disabled="isLifecycleFinalized || !selectedDistanceId"
            @import-applied="onResultsImportApplied"
          />

          <results-grid
            :results="results"
            :checkpoints="checkpointsList"
            :official-time-mode="config.officialTimeMode || 'gunTime'"
            :filters="currentFilters"
            :is-exporting="isExporting"
            :server-pagination="true"
            :server-total-count="resultsTotalCount"
            :server-violation-count="resultsViolationCount"
            :server-total-pages="resultsTotalPages"
            :server-page="resultsPage"
            :server-per-page="resultsPerPage"
            :editing-checkpoint-cell="editingCheckpointCell"
            @open-status="openStatusModal"
            @edit-checkpoint="openCheckpointEditor"
            @view-details="openParticipantDetail"
            @export="exportResults"
            @page-change="onResultsPageChange"
            @per-page-change="onResultsPerPageChange"
          />

          <b-card
            v-if="hasResults"
            class="mt-3"
          >
            <h6>Массовое изменение статусов</h6>
            <div class="d-flex gap-2">
              <b-button
                :title="lifecycleLockTooltip || massDnsTooltip"
                variant="outline-warning"
                size="sm"
                :disabled="isLifecycleFinalized"
                @click="massDns"
              >
                Массовый НС (нет ни одной отметки)
              </b-button>
              <b-button
                :title="lifecycleLockTooltip || 'НФ для участников с отметками по трассе, но без финиша. Участники без меток сюда не попадают (используйте Массовый НС).'"
                variant="outline-danger"
                size="sm"
                :disabled="isLifecycleFinalized"
                @click="massDnf"
              >
                Массовый НФ (есть отметки, нет финиша)
              </b-button>
            </div>
          </b-card>
        </b-tab>

        <!-- Configuration Tab -->
        <b-tab title="⚙️ Конфигурация">
          <lifecycle-panel
            :start-id="startId"
            :distance-id="selectedDistanceId"
            @state-loaded="handleLifecycleStateLoaded"
            @changed="handleLifecycleChanged"
            @error="handleLifecycleError"
          />
          <config-panel
            :config="config"
            :available-distances="availableDistances"
            :current-distance="currentDistance"
            :invalid-participants-filter-request="invalidParticipantsFilterRequest"
            @update:config="handleConfigUpdate"
            @validation-changed="handleValidationChanged"
          />
        </b-tab>

        <!-- Diagnostics Tab -->
        <b-tab title="📊 Диагностика">
          <diagnostics-panel
            :diagnostics="diagnostics"
            :statistics="statistics"
          />

          <unmatched-readings
            :readings="unmatchedReadings"
            @assign-reading="handleAssignReading"
          />
        </b-tab>
      </b-tabs>

      <!-- Modals -->
      <status-selector
        :visible="modals.status.visible"
        :participant="modals.status.participant"
        @save="handleStatusSave"
        @close="closeStatusModal"
      />

      <checkpoint-editor
        :visible="modals.checkpoint.visible"
        :data="modals.checkpoint.data"
        :checkpoints="checkpointsList"
        :timezone="currentTimezone"
        :is-removing-override="isRemovingOverride"
        @select-alternative="handleAlternativeSelection"
        @save-manual-time="handleManualTime"
        @save-virtual-reading="handleVirtualReading"
        @remove-override="handleRemoveCheckpointOverride"
        @close="closeCheckpointEditor"
      />

      <participant-detail-modal
        :visible="modals.participantDetail.visible"
        :participant="modals.participantDetail.participant"
        :checkpoints="checkpointsList"
        :user-start-time="modals.participantDetail.userStartTime"
        :global-start-time="modals.participantDetail.globalStartTime"
        :official-time-mode="config.officialTimeMode || 'gunTime'"
        :time-display-mode="currentFilters.timeDisplayMode || 'split'"
        :timezone="currentTimezone"
        @close="closeParticipantDetail"
      />

      <b-modal
        v-model="modals.mockManagement.visible"
        title="Управление моками (внешний API)"
        size="lg"
        hide-footer
        @hidden="onMockModalHidden"
      >
        <mock-management-panel
          :start-id="startId"
          @error="handleMockError"
        />
      </b-modal>
    </div>
  </div>
</template>

<script>
import {
  BButton, BSpinner, BTabs, BTab, BCard, BRow, BCol, BFormGroup, BFormSelect, BFormSelectOption, BAlert, BIcon, BFormInput, BBadge,
} from 'bootstrap-vue'
import RaceAnalysisApiService from '@/services/race-analysis-api.service'
import AdminRealtimeService from '@/services/admin-realtime.service'
import RaceErrorHandler from '@/mixins/race-error-handler'
import ManipulationsMixin from './mixins/manipulationsMixin'
import AutoRefreshMixin from './mixins/autoRefreshMixin'
import {
  loadRefreshIntervalSec,
  saveRefreshIntervalSec,
  normalizeRefreshIntervalSec,
} from './utils/refresh-interval-storage'

// Components
import LiveMonitor from './components/monitoring/LiveMonitor.vue'
import DiagnosticsPanel from './components/monitoring/DiagnosticsPanel.vue'
import UnmatchedReadings from './components/monitoring/UnmatchedReadings.vue'
import StatusSelector from './components/editing/StatusSelector.vue'
import CheckpointEditor from './components/editing/CheckpointEditor.vue'
import ResultsGrid from './components/results/ResultsGrid.vue'
import ResultsImportPanel from './components/results/ResultsImportPanel.vue'
import ResultsFilters from './components/results/ResultsFilters.vue'
import ParticipantDetailModal from './components/results/ParticipantDetailModal.vue'
import ConfigPanel from './components/config/ConfigPanel.vue'
import MockManagementPanel from './components/config/MockManagementPanel.vue'
import LifecyclePanel from './components/lifecycle/LifecyclePanel.vue'
import { DEFAULT_TIMEZONE } from './utils/timezone'

/** StartPage already uses `tab` for main nav; sub-tabs use a separate key. */
const ANALYZER_TAB_QUERY_KEY = 'analyzerTab'
const ANALYZER_TAB_SLUGS = ['results', 'config', 'diagnostics']

function analyzerTabIndexFromQuery(slug) {
  if (slug == null || slug === '') return 0
  const i = ANALYZER_TAB_SLUGS.indexOf(String(slug).toLowerCase())
  return i >= 0 ? i : 0
}

function analyzerSlugFromIndex(index) {
  return ANALYZER_TAB_SLUGS[Number(index)] || 'results'
}

export default {
  name: 'RaceAnalyzerV2',
  components: {
    BButton,
    BSpinner,
    BTabs,
    BTab,
    BCard,
    BRow,
    BCol,
    BFormGroup,
    BFormSelect,
    BFormSelectOption,
    BAlert,
    BIcon,
    BFormInput,
    BBadge,
    LiveMonitor,
    DiagnosticsPanel,
    UnmatchedReadings,
    StatusSelector,
    CheckpointEditor,
    ResultsGrid,
    ResultsImportPanel,
    ResultsFilters,
    ParticipantDetailModal,
    ConfigPanel,
    MockManagementPanel,
    LifecyclePanel,
  },
  mixins: [RaceErrorHandler, ManipulationsMixin, AutoRefreshMixin],
  props: {
    startId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      // Core state
      config: this.getDefaultConfig(),
      results: [],
      unmatchedReadings: [],
      diagnostics: null,
      statistics: null,

      // Distance management
      availableDistances: [],
      selectedDistanceId: null,
      skipUnsavedDistanceConfirmOnce: false,
      hasInvalidDistances: false,
      invalidDistancesCount: 0,
      invalidParticipantsFilterRequest: 0,

      // Config change tracking
      hasUnsavedConfigChanges: false,
      savedConfigSnapshot: null,
      isSavingConfig: false,

      // UI state
      isLoading: false,
      isAnalyzing: false,
      isExporting: false,
      isApplyingChanges: false,
      isRemovingOverride: false,
      activeTab: 0,

      // Filters
      currentFilters: {
        search: '',
        gender: '',
        group: '',
        status: '',
        violationOnly: false,
        resultType: 'gun',
        chipTimeEnabled: true,
        timeDisplayMode: 'split',
      },

      // Server-side results (GET analyze-results)
      resultsTotalCount: 0,
      resultsViolationCount: 0,
      resultsPage: 1,
      resultsPerPage: 50,
      resultsTotalPages: 0,
      filterMetaGroups: [],

      // Modals
      modals: {
        status: { visible: false, participant: null },
        checkpoint: { visible: false, data: null },
        participantDetail: {
          visible: false, participant: null, userStartTime: null, globalStartTime: null,
        },
        mockManagement: { visible: false },
      },

      // Mock mode: true = mock from DB, false = real API, null = unknown
      mockMode: null,

      // Task 10: per-distance lifecycle state (loaded by LifecyclePanel)
      lifecycleState: null,

      // Theme
      layoutClass: 'light-layout',
      themeObserver: null,
      realtimeUnsubscribe: null,
      realtimeBusy: false,
      clientSessionId: `ra2-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
    }
  },
  computed: {
    hasResults() {
      return (this.resultsTotalCount > 0) || (this.results && this.results.length > 0)
    },

    checkpointsList() {
      return this.config && this.config.track && this.config.track.checkpoints
        ? this.config.track.checkpoints
        : []
    },

    /** Two result columns (gun + chip segment) when distance uses chip time and track has ≥2 CPs */
    chipTimeDualResultColumns() {
      const mode = this.config && this.config.officialTimeMode
        ? this.config.officialTimeMode
        : 'gunTime'
      return mode === 'chipTime' && (this.checkpointsList || []).length >= 2
    },

    massDnsTooltip() {
      return 'НС для участников без единой отметки по трассе. Участники с отметками, но без финиша попадают под массовый НФ.'
    },

    currentTimezone() {
      return this.config && this.config.timezone
        ? this.config.timezone
        : DEFAULT_TIMEZONE
    },

    /**
     * Highlights the results grid cell while the checkpoint editor modal is open.
     * Note: admin analyze-results reload excludes userId from row attributes, so the grid
     * often has no item.userId — we also pass num for matching on bib number.
     */
    editingCheckpointCell() {
      if (!this.modals.checkpoint.visible || !this.modals.checkpoint.data) return null
      const { participant, checkpoint } = this.modals.checkpoint.data
      if (!participant || !checkpoint) return null
      return {
        userId: participant.userId,
        num: participant.num,
        point: checkpoint.point,
        lap: checkpoint.lap != null ? checkpoint.lap : 0,
      }
    },

    distanceOptions() {
      return this.availableDistances.map(d => ({
        value: d.id,
        text: d.name,
      }))
    },

    /** Mirrors <b-tab> titles; shown in sticky toolbar (real <b-tabs> nav is hidden). */
    mainAnalyzerTabs() {
      return [
        { key: 'results', title: '📋 Результаты' },
        { key: 'config', title: '⚙️ Конфигурация' },
        { key: 'diagnostics', title: '📊 Диагностика' },
      ]
    },

    currentDistance() {
      if (!this.selectedDistanceId) return null
      return this.availableDistances.find(d => d.id === this.selectedDistanceId)
    },

    /** Full-screen message while reprocess / analysis runs (priority: remove override → batch → analysis). */
    operationBlockingUi() {
      if (this.isRemovingOverride) {
        return {
          title: 'Снимаем ручное время',
          hint: 'Удаляем переопределение в конфигурации, заново считаем результаты по данным хронометража и обновляем таблицу. Обычно это несколько секунд.',
        }
      }
      if (this.isApplyingChanges) {
        return {
          title: 'Применяем ручные корректировки',
          hint: 'Отправляем изменения на сервер, выполняется полный пересчёт дистанции и загрузка результатов. Не закрывайте вкладку.',
        }
      }
      if (this.isAnalyzing) {
        return {
          title: 'Выполняется пересчёт',
          hint: 'Запущен анализ / обновление результатов. Дождитесь завершения.',
        }
      }
      return null
    },

    /** Short line for the sticky Live monitor bar */
    operationBlockingShortLabel() {
      if (this.isRemovingOverride) return 'Снятие ручного времени…'
      if (this.isApplyingChanges) return 'Применение корректировок…'
      if (this.isAnalyzing) return 'Пересчёт результатов…'
      return ''
    },

    validDistancesCount() {
      if (!this.config || !this.config.userConfigs) return 0
      return this.config.userConfigs.filter(p => p.distanceId).length
    },

    /** Task 10: derived flag used to lock destructive admin controls. */
    isLifecycleFinalized() {
      return !!(this.lifecycleState && this.lifecycleState.lifecycleStatus === 'finalized')
    },

    /** Task 10: tooltip shown on locked controls. */
    lifecycleLockTooltip() {
      return this.isLifecycleFinalized
        ? 'Дистанция финализирована. Снимите финализацию во вкладке «Конфигурация», чтобы внести правки.'
        : ''
    },
  },
  watch: {
    startId: {
      immediate: true,
      async handler(newId) {
        if (newId) {
          await Promise.all([this.loadDistances(), this.loadMockMode()])
        }
      },
    },

    selectedDistanceId: {
      immediate: true,
      async handler(newDistanceId, oldDistanceId) {
        if (!newDistanceId) return
        if (this.skipUnsavedDistanceConfirmOnce) {
          this.skipUnsavedDistanceConfirmOnce = false
          return
        }

        // Only check for unsaved changes if this is a distance change (not initial selection)
        if (oldDistanceId && newDistanceId !== oldDistanceId) {
          // Check for unsaved changes
          if (this.pendingManipulations && this.pendingManipulations.length > 0) {
            const confirmed = await this.$bvModal.msgBoxConfirm(
              'У вас есть несохраненные изменения. Они будут потеряны при смене дистанции.',
              {
                title: 'Несохраненные изменения',
                okTitle: 'Продолжить',
                cancelTitle: 'Отмена',
                okVariant: 'warning',
              },
            )

            if (!confirmed) {
              // Revert selection
              this.$nextTick(() => {
                this.skipUnsavedDistanceConfirmOnce = true
                this.selectedDistanceId = oldDistanceId
              })
              return
            }
          }
        }

        // Load data for the selected distance
        await this.loadState()
        this.subscribeRealtime()
      },
    },

    config: {
      deep: true,
      handler(newConfig) {
        // Skip if no snapshot yet (initial load)
        if (!this.savedConfigSnapshot) {
          return
        }

        // Compare with snapshot
        const currentSnapshot = JSON.stringify(newConfig)
        this.hasUnsavedConfigChanges = (currentSnapshot !== this.savedConfigSnapshot)
      },
    },

    activeTab(newVal) {
      this.syncAnalyzerTabQuery(newVal)
      if (
        newVal === 2
        && this.startId
        && this.selectedDistanceId
        && (this.statistics == null || this.diagnostics == null)
      ) {
        this.loadDiagnosticsFromState().catch(e => console.warn('[RaceAnalyzerV2] diagnostics load failed', e))
      }
    },

    '$route.query.analyzerTab': function (newSlug) {
      const idx = analyzerTabIndexFromQuery(newSlug)
      if (idx !== this.activeTab) {
        this.activeTab = idx
      }
    },
  },
  async created() {
    this.applyAnalyzerTabFromRoute()
    // loadDistances and loadState will be called by watcher
  },
  mounted() {
    this.refreshIntervalSec = loadRefreshIntervalSec()

    // Don't start auto-refresh on mount (user controls it)
    // this.startAutoRefresh(() => this.refreshData())

    // Ensure URL reflects tab (e.g. default results) for reload consistency
    this.syncAnalyzerTabQuery(this.activeTab)

    // Setup theme observer
    this.setupThemeObserver()
    this.setupRealtime()
  },

  beforeDestroy() {
    // Cleanup theme observer
    if (this.themeObserver) {
      this.themeObserver.disconnect()
    }
    this.teardownRealtime()
  },

  methods: {
    getFilterStorageKey() {
      return `race-analyzer-v2-filters:${this.startId || 'unknown'}:${this.selectedDistanceId || 'all'}`
    },

    restoreFiltersFromSession() {
      try {
        const raw = sessionStorage.getItem(this.getFilterStorageKey())
        if (!raw) return
        const parsed = JSON.parse(raw)
        if (!parsed || typeof parsed !== 'object') return
        this.currentFilters = {
          ...this.currentFilters,
          ...parsed,
          chipTimeEnabled: parsed.chipTimeEnabled !== false,
        }
      } catch (e) {
        // Ignore invalid persisted filters
      }
    },

    persistFiltersToSession(filters) {
      try {
        sessionStorage.setItem(this.getFilterStorageKey(), JSON.stringify(filters || {}))
      } catch (e) {
        // Ignore storage errors
      }
    },

    applyAnalyzerTabFromRoute() {
      const slug = this.$route.query[ANALYZER_TAB_QUERY_KEY]
      const idx = analyzerTabIndexFromQuery(slug)
      if (idx !== this.activeTab) {
        this.activeTab = idx
      }
    },

    syncAnalyzerTabQuery(tabIndex) {
      const slug = analyzerSlugFromIndex(tabIndex)
      if (this.$route.query[ANALYZER_TAB_QUERY_KEY] === slug) {
        return
      }
      this.$router
        .replace({
          name: this.$route.name,
          params: { ...this.$route.params },
          query: { ...this.$route.query, [ANALYZER_TAB_QUERY_KEY]: slug },
        })
        .catch(() => {})
    },

    detectTheme() {
      const html = document.documentElement
      const { body } = document

      if (html.classList.contains('dark-layout')
          || body.classList.contains('dark-layout')
          || html.classList.contains('theme-dark')) {
        this.layoutClass = 'dark-layout'
      } else {
        this.layoutClass = 'light-layout'
      }
    },

    setupThemeObserver() {
      // Initial detection
      this.detectTheme()

      // Watch for theme changes on html and body
      const observerConfig = { attributes: true, attributeFilter: ['class'] }

      this.themeObserver = new MutationObserver(() => {
        this.detectTheme()
      })

      this.themeObserver.observe(document.documentElement, observerConfig)
      this.themeObserver.observe(document.body, observerConfig)
    },

    setupRealtime() {
      this.realtimeUnsubscribe = AdminRealtimeService.onEvent(this.handleRealtimeEvent)
      this.subscribeRealtime()
    },

    teardownRealtime() {
      if (this.realtimeUnsubscribe) {
        this.realtimeUnsubscribe()
        this.realtimeUnsubscribe = null
      }
      AdminRealtimeService.destroy()
    },

    subscribeRealtime() {
      if (!this.startId || this.selectedDistanceId == null) return
      AdminRealtimeService.subscribe({
        startId: Number(this.startId),
        distanceId: Number(this.selectedDistanceId),
      })
    },

    async handleRealtimeEvent(event) {
      if (!event || !event.type || !event.scope) return
      const sameScope = Number(event.scope.startId) === Number(this.startId)
        && Number(event.scope.distanceId) === Number(this.selectedDistanceId)
      if (!sameScope) return
      if (event.type === 'race.analysis.config_saved') {
        if (this.realtimeBusy) return
        this.realtimeBusy = true
        try {
          await this.reloadConfigFromServer()
          await this.loadFilterMeta()
          await this.loadResultsPage(this.resultsPage || 1)
          if (this.activeTab === 2) {
            await this.loadDiagnosticsFromState()
          }
          const sourceClientSessionId = event.payload && event.payload.clientSessionId
            ? String(event.payload.clientSessionId)
            : null
          if (sourceClientSessionId && sourceClientSessionId === this.clientSessionId) {
            return
          }
          // Skip notification only on the tab that is currently performing save.
          // Other tabs (even under the same admin account) should still notify.
          if (!this.isSavingConfig) {
            const currentAdminId = this.getCurrentAdminId()
            const actorUserId = event.actor && event.actor.userId != null
              ? Number(event.actor.userId)
              : null
            if (actorUserId !== null && currentAdminId !== null && actorUserId !== currentAdminId) {
              this.showInfoToast(
                'Возможно, другой администратор изменил конфигурацию в параллельной сессии. Конфигурация и данные автоматически обновлены.',
                'Обновление из другой сессии',
              )
            } else {
              this.showInfoToast(
                'Конфигурация была изменена в другой вкладке или сессии. Текущие данные автоматически синхронизированы.',
                'Синхронизация конфигурации',
              )
            }
          }
        } catch (error) {
          this.handleRaceApiError(error, 'Realtime sync')
        } finally {
          this.realtimeBusy = false
        }
        return
      }
      if (event.type === 'race.analysis.started') {
        this.isAnalyzing = true
        return
      }
      if (event.type === 'race.analysis.failed') {
        this.isAnalyzing = false
        const message = (event.payload && event.payload.message) || 'Ошибка realtime операции'
        this.showErrorToast(message, 'Realtime')
        return
      }
      if (event.type === 'race.analysis.completed' || event.type === 'race.analysis.state_changed') {
        if (this.realtimeBusy) return
        this.realtimeBusy = true
        try {
          await this.reloadConfigFromServer()
          await this.loadFilterMeta()
          await this.loadResultsPage(this.resultsPage || 1)
          if (this.activeTab === 2) {
            await this.loadDiagnosticsFromState()
          }
        } catch (error) {
          this.handleRaceApiError(error, 'Realtime sync')
        } finally {
          this.isAnalyzing = false
          this.realtimeBusy = false
        }
      }
    },

    getCurrentAdminId() {
      try {
        const userData = JSON.parse(localStorage.getItem('userData') || '{}')
        if (userData && userData.id != null && String(userData.id).trim() !== '') {
          return Number(userData.id)
        }
      } catch (e) {
        // Ignore malformed user data
      }
      return null
    },

    getDefaultConfig() {
      return {
        startTime: null,
        track: { checkpoints: [] },
        userConfigs: [],
        externalStartId: null,
        timezone: DEFAULT_TIMEZONE,
        analysisParams: {
          repeatDetectionInterval: 5,
          priorityRule: 'min',
        },
        virtualReadings: [],
        participantStatuses: {},
        checkpointTimeConstraints: {},
      }
    },

    /**
     * Load available distances for the start
     */
    async loadDistances() {
      if (!this.startId) {
        console.warn('[RaceAnalyzerV2] No startId provided, skipping distances load')
        return
      }

      try {
        const distances = await RaceAnalysisApiService.getDistancesByStartId(this.startId)
        this.availableDistances = distances || []

        // Auto-select first distance if available and none selected
        if (this.availableDistances.length > 0 && !this.selectedDistanceId) {
          this.selectedDistanceId = this.availableDistances[0].id
        }

        console.log('[RaceAnalyzerV2] Loaded distances:', this.availableDistances)
      } catch (error) {
        console.error('[RaceAnalyzerV2] Error loading distances:', error)
        this.handleRaceApiError(error, 'Загрузка дистанций')
      }
    },

    /**
     * Load config + paginated results from DB (no full analyzer run).
     */
    async loadState() {
      if (!this.startId) {
        console.warn('[RaceAnalyzerV2] No startId provided, skipping state load')
        return
      }

      if (!this.selectedDistanceId) {
        console.warn('[RaceAnalyzerV2] No distance selected, skipping state load')
        return
      }

      this.isLoading = true

      this.$set(this, 'results', [])
      this.resultsTotalCount = 0
      this.resultsViolationCount = 0
      this.resultsTotalPages = 0
      this.resultsPage = 1
      this.$set(this, 'unmatchedReadings', [])
      this.$set(this, 'diagnostics', null)
      this.$set(this, 'statistics', null)

      try {
        const prevStartTime = this.config && this.config.startTime
        const prevTimezone = this.config && this.config.timezone

        const [loadedConfig, filtersMeta] = await Promise.all([
          RaceAnalysisApiService.getConfigByDistance(this.startId, this.selectedDistanceId),
          RaceAnalysisApiService.getRaceAnalysisFiltersByDistance(this.startId, this.selectedDistanceId),
        ])

        this.filterMetaGroups = filtersMeta.groups || []

        const cfg = loadedConfig || this.getDefaultConfig()
        if (cfg.externalStartId === undefined) {
          cfg.externalStartId = null
        }
        if (cfg.startTime === undefined) {
          cfg.startTime = null
        }

        const emptyStart = cfg.startTime === undefined
          || cfg.startTime === null
          || cfg.startTime === ''
        if (emptyStart && prevStartTime) {
          cfg.startTime = prevStartTime
        }
        if (!cfg.timezone && prevTimezone) {
          cfg.timezone = prevTimezone
        }

        this.$set(this, 'config', cfg)

        this.savedConfigSnapshot = JSON.stringify(this.config)
        this.hasUnsavedConfigChanges = false
        this.restoreFiltersFromSession()

        const hasManipulations = !!(
          (cfg.virtualReadings && cfg.virtualReadings.length > 0)
          || (cfg.manualReplacements && Object.keys(cfg.manualReplacements).length > 0)
          || (cfg.checkpointOverrides && cfg.checkpointOverrides.length > 0)
          || (cfg.participantStatuses && Object.keys(cfg.participantStatuses).length > 0)
        )

        if (hasManipulations) {
          this.showInfoToast(
            'Загружена конфигурация с ручными корректировками',
            'Состояние восстановлено',
          )
        } else if (!cfg?.track?.checkpoints?.length) {
          this.showInfoToast(
            'Конфигурация для этой дистанции пуста. Настройте чекпоинты перед анализом.',
            'Новая дистанция',
          )
        }

        await this.loadResultsPage(1)
        await this.loadMockMode()

        // WS3: if the operator stays on «Диагностика», reload server state for the new distance
        // (otherwise stale per-distance counters can linger until tab switch).
        if (this.activeTab === 2) {
          await this.loadDiagnosticsFromState()
        }
      } catch (error) {
        this.handleRaceApiError(error, 'Загрузка состояния')
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Sync in-memory config with server after mutations (batch, analyze, reprocess, etc.).
     * Avoids stale participantStatuses overwriting DB on the next save/analyze.
     */
    async reloadConfigFromServer(options = {}) {
      const showManipulationsToast = options.showManipulationsToast === true
      if (!this.startId || !this.selectedDistanceId) return

      const prevStartTime = this.config && this.config.startTime
      const prevTimezone = this.config && this.config.timezone

      const loadedConfig = await RaceAnalysisApiService.getConfigByDistance(
        this.startId,
        this.selectedDistanceId,
      )

      const cfg = loadedConfig || this.getDefaultConfig()
      if (cfg.externalStartId === undefined) {
        cfg.externalStartId = null
      }
      if (cfg.startTime === undefined) {
        cfg.startTime = null
      }

      const emptyStart = cfg.startTime === undefined
        || cfg.startTime === null
        || cfg.startTime === ''
      if (emptyStart && prevStartTime) {
        cfg.startTime = prevStartTime
      }
      if (!cfg.timezone && prevTimezone) {
        cfg.timezone = prevTimezone
      }

      this.$set(this, 'config', cfg)
      this.savedConfigSnapshot = JSON.stringify(this.config)
      this.hasUnsavedConfigChanges = false

      if (showManipulationsToast) {
        const hasManipulations = !!(
          (cfg.virtualReadings && cfg.virtualReadings.length > 0)
          || (cfg.manualReplacements && Object.keys(cfg.manualReplacements).length > 0)
          || (cfg.checkpointOverrides && cfg.checkpointOverrides.length > 0)
          || (cfg.participantStatuses && Object.keys(cfg.participantStatuses).length > 0)
        )
        if (hasManipulations) {
          this.showInfoToast(
            'Загружена конфигурация с ручными корректировками',
            'Состояние восстановлено',
          )
        }
      }
    },

    async onResultsImportApplied() {
      await this.loadResultsPage(1)
      await this.loadFilterMeta()
    },

    async loadResultsPage(page = 1) {
      if (!this.startId || !this.selectedDistanceId) return

      const q = this.currentFilters || {}
      const res = await RaceAnalysisApiService.getResultsPageByDistance(
        this.startId,
        this.selectedDistanceId,
        {
          page,
          maxResultCount: this.resultsPerPage,
          filterText: q.search || '',
          gender: q.gender || '',
          group: q.group || '',
          violationOnly: !!q.violationOnly,
        },
      )

      this.$set(this, 'results', res.rows || [])
      this.resultsTotalCount = res.count
      this.resultsViolationCount = Number.isFinite(Number(res.violationCount))
        ? Number(res.violationCount)
        : 0
      this.resultsPage = res.page
      this.resultsTotalPages = res.totalPages
    },

    onResultsPageChange(page) {
      this.resultsPage = page
      this.loadResultsPage(page)
    },

    onResultsPerPageChange(val) {
      const n = parseInt(val, 10)
      if (!Number.isFinite(n) || n <= 0) return
      this.resultsPerPage = n
      this.loadResultsPage(1)
    },

    /** Full analyzer run — diagnostics, statistics, unmatched (heavy). */
    async loadDiagnosticsFromState() {
      if (!this.startId || !this.selectedDistanceId) return
      const state = await RaceAnalysisApiService.getState(this.startId, this.selectedDistanceId)
      this.$set(this, 'unmatchedReadings', state.unmatchedReadings || [])
      this.$set(this, 'diagnostics', state.diagnostics || null)
      this.$set(this, 'statistics', state.statistics != null ? state.statistics : null)
    },

    async loadMockMode() {
      try {
        const res = await RaceAnalysisApiService.getMockSettings()
        this.mockMode = res.useMock === true
      } catch {
        this.mockMode = null
      }
    },

    openMockManagement() {
      this.modals.mockManagement.visible = true
    },

    onMockModalHidden() {
      this.modals.mockManagement.visible = false
      this.loadMockMode()
    },

    /**
     * Save configuration without running analysis
     */
    async saveConfiguration() {
      if (!this.startId || !this.selectedDistanceId) {
        this.showErrorToast('Не выбрана дистанция', 'Ошибка')
        return
      }

      this.isSavingConfig = true

      try {
        await RaceAnalysisApiService.saveConfig(
          this.startId,
          this.selectedDistanceId,
          this.config,
          this.clientSessionId,
        )

        // Update snapshot
        this.savedConfigSnapshot = JSON.stringify(this.config)
        this.hasUnsavedConfigChanges = false

        this.showSuccessToast(
          'Конфигурация сохранена успешно',
          'Сохранено',
        )
      } catch (error) {
        this.handleRaceApiError(error, 'Сохранение конфигурации')
      } finally {
        this.isSavingConfig = false
      }
    },

    /**
     * Perform initial analysis
     */
    async performAnalysis() {
      if (this.isAnalyzing) return

      if (!this.startId) {
        this.showErrorToast('Старт не загружен', 'Ошибка')
        return
      }

      if (!this.selectedDistanceId) {
        this.showErrorToast('Выберите дистанцию перед анализом', 'Ошибка')
        return
      }

      if (this.hasInvalidDistances) {
        this.showErrorToast(
          `У ${this.invalidDistancesCount} участников указана несуществующая дистанция. Исправьте перед анализом.`,
          'Ошибка валидации',
        )
        return
      }

      // Auto-save config before analysis
      if (this.hasUnsavedConfigChanges) {
        await this.saveConfiguration()
      }

      this.isAnalyzing = true

      try {
        const result = await RaceAnalysisApiService.analyze(
          this.startId,
          this.config,
          [],
          this.selectedDistanceId,
        )

        this.$set(this, 'unmatchedReadings', result.unmatchedReadings || [])
        this.$set(this, 'diagnostics', result.diagnostics || null)
        this.$set(this, 'statistics', null)

        await this.loadFilterMeta()
        await this.loadResultsPage(1)

        await this.reloadConfigFromServer()

        this.showSuccessToast(
          `Анализ завершен. Участников: ${this.resultsTotalCount}`,
          'Успешно',
        )

        // Switch to results tab
        this.activeTab = 0
      } catch (error) {
        this.handleRaceApiError(error, 'Анализ забега')
      } finally {
        this.isAnalyzing = false
      }
    },

    /**
     * Perform analysis only for participants with valid distanceId
     */
    async performAnalysisForValidOnly() {
      if (this.isAnalyzing) return

      if (!this.startId) {
        this.showErrorToast('Старт не загружен', 'Ошибка')
        return
      }

      if (!this.selectedDistanceId) {
        this.showErrorToast('Выберите дистанцию перед анализом', 'Ошибка')
        return
      }

      // Filter config to only include participants with valid distanceId
      const validParticipants = this.config.userConfigs.filter(p => p.distanceId)

      if (validParticipants.length === 0) {
        this.showErrorToast('Нет участников с валидной дистанцией', 'Ошибка')
        return
      }

      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Будет выполнен анализ для ${validParticipants.length} участников с валидной дистанцией. ${this.invalidDistancesCount} участников будут пропущены. Продолжить?`,
        {
          title: 'Принудительный анализ',
          okTitle: 'Анализировать',
          cancelTitle: 'Отмена',
          okVariant: 'warning',
        },
      )

      if (!confirmed) return

      // Auto-save config before analysis
      if (this.hasUnsavedConfigChanges) {
        await this.saveConfiguration()
      }

      this.isAnalyzing = true

      try {
        // Full config must be sent: saveForDistance syncs race_analysis_participants from
        // request userConfigs (delete-all-then-upsert). Server already filters by distance for RaceAnalyzer.
        const result = await RaceAnalysisApiService.analyze(
          this.startId,
          this.config,
          [],
          this.selectedDistanceId,
        )

        this.$set(this, 'unmatchedReadings', result.unmatchedReadings || [])
        this.$set(this, 'diagnostics', result.diagnostics || null)
        this.$set(this, 'statistics', null)

        await this.loadFilterMeta()
        await this.loadResultsPage(1)

        await this.reloadConfigFromServer()

        this.showSuccessToast(
          `Анализ завершен. Всего в БД: ${this.resultsTotalCount}. Пропущено: ${this.invalidDistancesCount}`,
          'Успешно',
        )

        // Switch to results tab
        this.activeTab = 0
      } catch (error) {
        this.handleRaceApiError(error, 'Принудительный анализ')
      } finally {
        this.isAnalyzing = false
      }
    },

    /**
     * Refresh data (for auto-refresh)
     */
    async refreshData() {
      if (!this.startId) return
      if (!this.selectedDistanceId) return
      if (this.isAnalyzing) return

      this.isAnalyzing = true

      try {
        const result = await RaceAnalysisApiService.reprocess(this.startId, this.selectedDistanceId)

        const oldCount = this.resultsTotalCount
        this.$set(this, 'unmatchedReadings', result.unmatchedReadings)
        this.$set(this, 'diagnostics', result.diagnostics)
        this.$set(this, 'statistics', null)

        await this.loadFilterMeta()
        await this.loadResultsPage(this.resultsPage)

        await this.reloadConfigFromServer()

        const newCount = this.resultsTotalCount
        if (newCount !== oldCount) {
          this.showInfoToast(
            `Данные обновлены. Участников: ${newCount}`,
            'Автообновление',
          )
        }
      } catch (error) {
        if (this.isRaceAnalysisConflict(error)) {
          this.showInfoToast(
            'Анализ уже выполняется, повторите позже',
            'Автообновление',
          )
        } else {
          console.error('[RaceAnalyzerV2] Refresh failed:', error)
        }
        throw error
      } finally {
        this.isAnalyzing = false
      }
    },

    /**
     * Apply batch changes
     */
    async applyBatchChanges() {
      if (!this.hasChanges) return

      if (!this.startId) {
        this.showErrorToast('Старт не загружен', 'Ошибка')
        return
      }

      if (!this.selectedDistanceId) {
        this.showErrorToast('Выберите дистанцию', 'Ошибка')
        return
      }

      this.isApplyingChanges = true
      this.isAnalyzing = true

      try {
        const appliedCount = this.pendingManipulations.length

        const result = await RaceAnalysisApiService.applyBatchManipulations(
          this.startId,
          this.pendingManipulations,
          this.selectedDistanceId,
        )

        this.$set(this, 'unmatchedReadings', result.unmatchedReadings)
        this.$set(this, 'diagnostics', result.diagnostics)
        this.$set(this, 'statistics', null)

        await this.loadFilterMeta()
        await this.loadResultsPage(1)

        this.clearManipulations()

        await this.reloadConfigFromServer()

        this.showSuccessToast(
          `Применено изменений: ${appliedCount}`,
          'Изменения сохранены',
        )
      } catch (error) {
        this.handleRaceApiError(error, 'Применение изменений')
      } finally {
        this.isApplyingChanges = false
        this.isAnalyzing = false
      }
    },

    /**
     * Discard pending changes
     */
    discardChanges() {
      const count = this.changesCount
      this.clearManipulations()

      this.showInfoToast(
        `Отменено изменений: ${count}`,
        'Изменения отменены',
      )
    },

    async discardConfigChanges() {
      if (!this.hasUnsavedConfigChanges) return
      const confirmed = await this.$bvModal.msgBoxConfirm(
        'Локальные изменения конфигурации будут отменены и восстановится последнее сохраненное состояние. Продолжить?',
        {
          title: 'Сброс конфигурации',
          okTitle: 'Сбросить',
          cancelTitle: 'Отмена',
          okVariant: 'warning',
        },
      )
      if (!confirmed) return
      if (this.savedConfigSnapshot) {
        this.$set(this, 'config', JSON.parse(this.savedConfigSnapshot))
        this.hasUnsavedConfigChanges = false
        this.showInfoToast('Локальные изменения конфигурации отменены', 'Сброс выполнен')
      }
    },

    /**
     * Handle filters update
     */
    handleFiltersUpdate(filters) {
      this.currentFilters = filters
      this.persistFiltersToSession(filters)
      if (this._resultsFilterDebounce) {
        clearTimeout(this._resultsFilterDebounce)
      }
      this._resultsFilterDebounce = setTimeout(() => {
        this.loadResultsPage(1).catch(err => {
          this.handleRaceApiError(err, 'Загрузка результатов')
        })
      }, 350)
    },

    async loadFilterMeta() {
      if (!this.startId || !this.selectedDistanceId) return
      try {
        const filtersMeta = await RaceAnalysisApiService.getRaceAnalysisFiltersByDistance(
          this.startId,
          this.selectedDistanceId,
        )
        this.filterMetaGroups = filtersMeta.groups || []
      } catch (e) {
        console.warn('[RaceAnalyzerV2] loadFilterMeta failed', e)
      }
    },

    /**
     * Handle config update
     */
    handleConfigUpdate(newConfig) {
      this.$set(this, 'config', newConfig)
    },

    handleValidationChanged(data) {
      this.hasInvalidDistances = data.hasInvalidDistances
      this.invalidDistancesCount = data.invalidCount
    },

    openInvalidParticipantsInConfig() {
      this.invalidParticipantsFilterRequest += 1
      this.activeTab = 1
    },

    /**
     * Open status modal
     */
    openStatusModal(participant) {
      const userId = this.resolveParticipantUserId(participant)
      this.modals.status.participant = userId ? { ...participant, userId } : participant
      this.modals.status.visible = true
    },

    closeStatusModal() {
      this.modals.status.visible = false
      this.modals.status.participant = null
    },

    /**
     * Handle status save
     */
    handleStatusSave(data) {
      const { participant } = this.modals.status
      const userId = data.userId !== undefined && data.userId !== null && String(data.userId).trim() !== ''
        ? String(data.userId).trim()
        : this.resolveParticipantUserId(participant)

      if (!userId) {
        this.showErrorToast(
          'Не удалось определить участника (userId). В строке результатов нет id — сопоставьте номер с конфигурацией или обновите страницу.',
          'Ошибка',
        )
        return
      }

      this.addManipulation('status', { userId, status: data.status })
      this.closeStatusModal()

      this.showSuccessToast(
        'Статус добавлен в очередь изменений',
        'Добавлено',
      )
    },

    /**
     * Open checkpoint editor
     */
    openCheckpointEditor(data) {
      this.modals.checkpoint.data = data
      this.modals.checkpoint.visible = true
    },

    closeCheckpointEditor() {
      this.modals.checkpoint.visible = false
      this.modals.checkpoint.data = null
    },

    /**
     * Open participant detail modal
     */
    openParticipantDetail(participant) {
      const userId = this.resolveParticipantUserId(participant)
      const normalizeStart = v => {
        if (v === undefined || v === null) return null
        if (v instanceof Date) return v.toISOString()
        const s = String(v)
        if (!s || s === '—' || s === 'null') return null
        return s
      }

      const userCfg = userId
        ? (this.config.userConfigs || []).find(u => u.userId === userId)
        : null

      this.modals.participantDetail.participant = participant
      this.modals.participantDetail.userStartTime = normalizeStart(userCfg && userCfg.startTime)
      this.modals.participantDetail.globalStartTime = normalizeStart(this.config && this.config.startTime)
      this.modals.participantDetail.visible = true
    },

    closeParticipantDetail() {
      this.modals.participantDetail.visible = false
      this.modals.participantDetail.participant = null
      this.modals.participantDetail.userStartTime = null
      this.modals.participantDetail.globalStartTime = null
    },

    /**
     * Paginated analyze-results rows may omit userId; JSON.stringify drops undefined.
     * Resolve stable user id from config.userConfigs (bib + distance) or fall back to num.
     */
    resolveParticipantUserId(participant) {
      if (!participant) return null
      const raw = participant.userId
      if (raw !== undefined && raw !== null && String(raw).trim() !== '') {
        return String(raw)
      }
      const { num } = participant
      const distanceId = this.selectedDistanceId
      const users = (this.config && this.config.userConfigs) || []
      const match = users.find(u => {
        const sameNum = num != null && num !== '' && String(u.num) === String(num)
        const sameDistance = distanceId == null || u.distanceId === distanceId
        return sameNum && sameDistance
      })
      if (match && match.userId != null && String(match.userId).trim() !== '') {
        return String(match.userId)
      }
      if (num != null && num !== '') {
        return String(num)
      }
      return null
    },

    /**
     * Handle alternative selection
     */
    handleAlternativeSelection(data) {
      const userId = this.resolveParticipantUserId(data.participant)
      if (!userId) {
        this.showErrorToast('Не удалось определить участника (userId). Проверьте конфигурацию.', 'Ошибка')
        return
      }
      this.addManipulation('checkpoint_time', {
        userId,
        point: data.checkpoint.point,
        time: data.timestamp,
        lap: data.checkpoint.lap,
      })

      this.closeCheckpointEditor()
      this.showSuccessToast('Время добавлено в очередь изменений', 'Добавлено')
    },

    /**
     * Handle manual time
     */
    handleManualTime(data) {
      const userId = this.resolveParticipantUserId(data.participant)
      if (!userId) {
        this.showErrorToast('Не удалось определить участника (userId). Проверьте конфигурацию.', 'Ошибка')
        return
      }
      this.addManipulation('checkpoint_time', {
        userId,
        point: data.checkpoint.point,
        time: data.time,
        lap: data.checkpoint.lap,
      })

      this.closeCheckpointEditor()
      this.showSuccessToast('Время добавлено в очередь изменений', 'Добавлено')
    },

    /**
     * Remove applied checkpoint time override (saved in config)
     */
    async handleRemoveCheckpointOverride({ participant, checkpoint }) {
      if (!this.startId) {
        this.showErrorToast('Старт не загружен', 'Ошибка')
        return
      }
      if (!this.selectedDistanceId) {
        this.showErrorToast('Выберите дистанцию', 'Ошибка')
        return
      }

      this.isRemovingOverride = true
      this.isAnalyzing = true

      try {
        const userId = this.resolveParticipantUserId(participant)
        if (!userId) {
          this.showErrorToast('Не удалось определить участника (userId). Проверьте конфигурацию.', 'Ошибка')
          return
        }

        const result = await RaceAnalysisApiService.removeCheckpointOverride(
          this.startId,
          {
            userId,
            point: checkpoint.point,
            lap: checkpoint.lap,
          },
          this.selectedDistanceId,
        )

        this.$set(this, 'unmatchedReadings', result.unmatchedReadings)
        this.$set(this, 'diagnostics', result.diagnostics)
        this.$set(this, 'statistics', null)

        await this.loadFilterMeta()
        await this.loadResultsPage(this.resultsPage)

        await this.reloadConfigFromServer()

        this.closeCheckpointEditor()
        this.showSuccessToast('Ручное время снято, результаты пересчитаны', 'Готово')
      } catch (error) {
        this.handleRaceApiError(error, 'Снятие ручного времени')
      } finally {
        this.isRemovingOverride = false
        this.isAnalyzing = false
      }
    },

    /**
     * Handle virtual reading
     */
    handleVirtualReading(data) {
      this.addManipulation('virtual_reading', data)

      this.closeCheckpointEditor()
      this.showSuccessToast('Виртуальное считывание добавлено', 'Добавлено')
    },

    /**
     * Handle unmatched reading assignment
     */
    async handleAssignReading(reading) {
      const participants = this.config.userConfigs || []
      if (!participants.length) {
        this.showErrorToast('Нет доступных участников')
        return
      }

      const options = participants.map(p => ({
        value: p.userId,
        text: `#${p.num || p.userId} ${p.name || ''} ${p.secondName || ''}`.trim(),
      }))

      const selectedUserId = await new Promise(resolve => {
        const items = options.map(o => `${o.text}`).join('\n')
        const input = prompt(`Выберите участника для назначения считывания:\n\n${items}\n\nВведите номер участника:`)
        if (!input) return resolve(null)
        const match = participants.find(p => p.num === input || p.userId === input)
        resolve(match ? match.userId : null)
      })

      if (!selectedUserId) return

      const chipId = reading.code
      const targetUser = participants.find(p => p.userId === selectedUserId)
      if (!targetUser || !targetUser.chipIds?.length) {
        this.showErrorToast('У выбранного участника нет чипов')
        return
      }

      this.addManipulation('reassign', {
        readingKey: `${reading.timestamp}-${reading.point}-${reading.code}`,
        fromUserId: null,
        toUserId: selectedUserId,
      })

      this.showSuccessToast(`Считывание назначено: ${targetUser.num || targetUser.userId}`)
    },

    async massDns() {
      if (!this.selectedDistanceId) {
        this.showErrorToast('Сначала выберите дистанцию')
        return
      }
      try {
        const preview = await RaceAnalysisApiService.massDns(this.startId, this.selectedDistanceId, true)
        if (!preview.affected || !preview.affected.length) {
          this.showInfoToast('Нет участников для массового НС')
          return
        }
        const names = preview.affectedNames || preview.affected.map(a => a.name || a.num || a.userId).join(', ')
        if (!confirm(`Установить НС для ${preview.affected.length} участников?\n\n${names}`)) return
        await RaceAnalysisApiService.massDns(this.startId, this.selectedDistanceId, false)
        this.showSuccessToast(`НС установлен для ${preview.affected.length} участников`)
        await this.loadFilterMeta()
        await this.loadResultsPage(1)
        await this.reloadConfigFromServer()
        await this.loadDiagnosticsFromState()
      } catch (e) {
        this.showErrorToast(`Не удалось применить массовый НС: ${e.message || e}`)
      }
    },

    async massDnf() {
      if (!this.selectedDistanceId) {
        this.showErrorToast('Сначала выберите дистанцию')
        return
      }
      try {
        const preview = await RaceAnalysisApiService.massDnf(this.startId, this.selectedDistanceId, true)
        if (!preview.affected || !preview.affected.length) {
          this.showInfoToast('Нет участников для массового НФ')
          return
        }
        const names = preview.affectedNames || preview.affected.map(a => a.name || a.num || a.userId).join(', ')
        if (!confirm(`Установить НФ для ${preview.affected.length} участников?\n\n${names}`)) return
        await RaceAnalysisApiService.massDnf(this.startId, this.selectedDistanceId, false)
        this.showSuccessToast(`НФ установлен для ${preview.affected.length} участников`)
        await this.loadFilterMeta()
        await this.loadResultsPage(1)
        await this.reloadConfigFromServer()
        await this.loadDiagnosticsFromState()
      } catch (e) {
        this.showErrorToast(`Не удалось применить массовый НФ: ${e.message || e}`)
      }
    },

    /**
     * Export results
     */
    async exportResults() {
      if (!this.startId) {
        this.showErrorToast('Старт не загружен', 'Ошибка')
        return
      }

      if (this.isExporting) {
        return // Prevent multiple simultaneous exports
      }

      this.isExporting = true

      try {
        const response = await RaceAnalysisApiService.exportCsv(
          this.startId,
          this.selectedDistanceId || null,
        )

        const disposition = response.headers['content-disposition'] || ''
        let filename = 'race-results.csv'
        const match = disposition.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i)
        if (match) {
          filename = decodeURIComponent(match[1] || match[2])
        }

        const blob = new Blob([response.data], { type: response.headers['content-type'] })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', filename)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)

        this.showSuccessToast('Файл загружен', 'Экспорт')
      } catch (error) {
        this.handleRaceApiError(error, 'Экспорт результатов')
      } finally {
        this.isExporting = false
      }
    },

    /**
     * Toggle auto-refresh
     */
    async toggleAutoRefresh() {
      if (this.isAnalyzing || this.isRefreshing) {
        this.showInfoToast(
          'Дождитесь завершения текущего пересчёта',
          'Автообновление',
        )
        return
      }

      if (this.isPaused) {
        // If no results yet, perform initial analysis
        if (!this.hasResults) {
          await this.performAnalysis()
          if (!this.hasResults) {
            return
          }
        }

        this.startAutoRefresh(() => this.refreshData())
        this.showInfoToast('Автообновление запущено', 'Live мониторинг')
      } else {
        this.stopAutoRefresh()
        this.showInfoToast('Автообновление остановлено', 'Пауза')
      }
    },

    handleRefreshIntervalChange(nextIntervalSec) {
      const parsed = normalizeRefreshIntervalSec(nextIntervalSec)
      if (parsed == null) {
        return
      }

      saveRefreshIntervalSec(parsed)
      this.applyRefreshIntervalSec(parsed, () => this.refreshData())
    },

    isRaceAnalysisConflict(error) {
      return !!(error && error.response && error.response.status === 409)
    },

    /**
     * Manual refresh
     */
    async manualRefresh() {
      if (this.isAnalyzing || this.isRefreshing) {
        this.showInfoToast(
          'Дождитесь завершения текущего пересчёта',
          'Автообновление',
        )
        return
      }

      // If no results yet, perform initial analysis
      if (!this.hasResults) {
        await this.performAnalysis()
      } else {
        await this.performRefresh(() => this.refreshData())
      }
    },

    handleMockError(message) {
      this.showErrorToast(message || 'Ошибка мока', 'Моки')
    },

    /** Task 10: keep local lifecycle flag in sync with LifecyclePanel. */
    handleLifecycleStateLoaded(state) {
      this.lifecycleState = state || null
    },

    handleLifecycleChanged(state) {
      this.lifecycleState = state || null
      const label = state && state.lifecycleStatus === 'finalized'
        ? 'Дистанция финализирована'
        : 'Финализация снята'
      this.showSuccessToast(label, 'Жизненный цикл')
    },

    handleLifecycleError(error) {
      this.handleRaceApiError(error, 'Жизненный цикл дистанции')
    },
  },
}
</script>

<style scoped>
.race-analyzer-v2 {
  position: relative;
  overflow: visible;
  background: transparent;
}

.loading-overlay {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  color: #b9b9c3;
}

.loading-overlay p {
  color: #6e6b7b;
  font-weight: 500;
}

/* Full-screen wait state: manual corrections / reprocess / analysis */
.ra-operation-overlay {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  background: rgba(15, 17, 26, 0.48);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
}

.ra-operation-overlay.light-layout .ra-operation-overlay__panel {
  background: #fff;
  border: 1px solid #ebe9f1;
  box-shadow: 0 12px 40px rgba(34, 41, 47, 0.18);
}

.ra-operation-overlay.dark-layout .ra-operation-overlay__panel {
  background: #283046;
  border: 1px solid #3b4253;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.35);
}

.ra-operation-overlay__panel {
  max-width: 420px;
  width: 100%;
  border-radius: 0.5rem;
  padding: 1.75rem 1.5rem;
  text-align: center;
}

.ra-operation-overlay__title {
  font-weight: 600;
  color: #3f3b4f;
  font-size: 1.05rem;
}

.ra-operation-overlay.dark-layout .ra-operation-overlay__title {
  color: #e7e9ed;
}

.ra-operation-overlay__hint {
  font-size: 0.9rem;
  line-height: 1.45;
  color: #6e6b7b;
}

.ra-operation-overlay.dark-layout .ra-operation-overlay__hint {
  color: #b4b7bd;
}

.ra-operation-fade-enter-active,
.ra-operation-fade-leave-active {
  transition: opacity 0.2s ease;
}

.ra-operation-fade-enter,
.ra-operation-fade-leave-to {
  opacity: 0;
}
</style>

<style>
/* Sticky toolbar: Vuexy navbar offset matches LiveMonitor (was sticky here alone) */
.race-analyzer-v2__sticky-toolbar {
  position: -webkit-sticky;
  position: sticky;
  top: calc(5rem + 15px);
  z-index: 10;
  margin-bottom: 1.5rem;
  margin-top: -0.5rem;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
}

.race-analyzer-v2__sticky-toolbar.light-layout {
  background: rgba(255, 255, 255, 0.94);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.race-analyzer-v2__sticky-toolbar.dark-layout {
  background: rgba(22, 26, 45, 0.94);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

.race-analyzer-v2__sticky-toolbar .live-monitor-wrapper {
  margin-bottom: 0.75rem;
}

/* Visually merge sticky tab strip with tab content card below */
.ra-tabstrip-merge-bottom {
  border-bottom-left-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
  margin-bottom: 0 !important;
}

.race-analyzer-v2__main-tabs.card > .card-header {
  display: none !important;
  padding: 0 !important;
  border: none !important;
  min-height: 0 !important;
}

.race-analyzer-v2__main-tabs.card {
  border-top-left-radius: 0 !important;
  border-top-right-radius: 0 !important;
  margin-top: -1px !important;
}

.race-analyzer-v2__sticky-tabstrip .nav-tabs .nav-link {
  border: 1px solid transparent;
  border-top-left-radius: 0.358rem;
  border-top-right-radius: 0.358rem;
  color: #6e6b7b;
  font-weight: 500;
  font-size: 0.9375rem;
  padding: 0.65rem 1rem;
  background: transparent;
  cursor: pointer;
}

.race-analyzer-v2__sticky-tabstrip .nav-tabs .nav-link:hover {
  color: #7367f0;
  border-color: transparent;
}

.race-analyzer-v2__sticky-tabstrip .nav-tabs .nav-link.active {
  color: #7367f0 !important;
  font-weight: 600;
  background: transparent !important;
  border-color: #ebe9f1 #ebe9f1 transparent !important;
}

.race-analyzer-v2.dark-layout .race-analyzer-v2__sticky-tabstrip .nav-tabs .nav-link {
  color: #a8aaae;
}

.race-analyzer-v2.dark-layout .race-analyzer-v2__sticky-tabstrip .nav-tabs .nav-link:hover {
  color: #d0d2d6;
}

.race-analyzer-v2.dark-layout .race-analyzer-v2__sticky-tabstrip .nav-tabs .nav-link.active {
  color: #b4a9ff !important;
  border-color: #3b4253 #3b4253 transparent !important;
}

/* Global styles for sticky to work (override bootstrap-vue) */
.race-analyzer-v2 .card,
.race-analyzer-v2 .tab-content,
.race-analyzer-v2 .tab-pane {
  overflow: visible !important;
}

/* Paddings for alerts/hints */
.race-analyzer-v2 .alert {
  padding: 1rem 1.25rem !important;
}

.race-analyzer-v2 .alert .b-icon {
  margin-right: 0.5rem;
}

/* Theme-aware styles for distance selector card */
.race-analyzer-v2 .distance-selector-card .card-body {
  background: transparent !important;
}

/* Dark theme for distance selector card */
.race-analyzer-v2.dark-layout .distance-selector-card {
  background: linear-gradient(118deg, #1e2139, #161d31) !important;
  border: 1px solid #3b4253 !important;
}

.race-analyzer-v2.dark-layout .distance-selector-card label,
.race-analyzer-v2.dark-layout .distance-selector-card .text-muted {
  color: #d0d2d6 !important;
}

.race-analyzer-v2.dark-layout .distance-selector-card .font-weight-bold {
  color: #fff !important;
}

/* Light theme for distance selector card */
.race-analyzer-v2.light-layout .distance-selector-card {
  background: #fff !important;
  border: 1px solid #ebe9f1 !important;
}

.race-analyzer-v2.light-layout .distance-selector-card label,
.race-analyzer-v2.light-layout .distance-selector-card .text-muted {
  color: #5e5873 !important;
}

/* Cards - dark theme */
.race-analyzer-v2.dark-layout .card {
  background: linear-gradient(118deg, #1e2139, #161d31) !important;
  border: 1px solid #3b4253 !important;
  color: #d0d2d6;
}

.race-analyzer-v2.dark-layout .card-body {
  background: transparent !important;
}

.race-analyzer-v2.dark-layout .card-title,
.race-analyzer-v2.dark-layout h6,
.race-analyzer-v2.dark-layout h5 {
  color: #fff !important;
}

.race-analyzer-v2.dark-layout label {
  color: #d0d2d6 !important;
}

.race-analyzer-v2.dark-layout .text-muted,
.race-analyzer-v2.dark-layout small {
  color: #a8aaae !important;
}

.race-analyzer-v2.dark-layout hr {
  border-top: 1px solid #3b4253 !important;
}

.race-analyzer-v2.dark-layout .form-control {
  background: #1a1e34 !important;
  border: 1px solid #3b4253 !important;
  color: #d0d2d6 !important;
}

.race-analyzer-v2.dark-layout .form-control:focus {
  background: #1e2239 !important;
  border-color: #7367f0 !important;
  color: #fff !important;
}

.race-analyzer-v2.dark-layout .custom-select {
  background: #1a1e34 !important;
  border: 1px solid #3b4253 !important;
  color: #d0d2d6 !important;
}

.race-analyzer-v2.dark-layout .custom-select:focus {
  background: #1e2239 !important;
  border-color: #7367f0 !important;
}

.race-analyzer-v2.dark-layout .custom-checkbox .custom-control-label {
  color: #d0d2d6 !important;
}

.race-analyzer-v2.dark-layout .custom-checkbox .custom-control-input:checked ~ .custom-control-label::before {
  background-color: #7367f0 !important;
  border-color: #7367f0 !important;
}

/* Light theme - cards and controls */
.race-analyzer-v2.light-layout .card {
  background: #fff !important;
  border: 1px solid #ebe9f1 !important;
  color: #5e5873;
}

.race-analyzer-v2.light-layout .card-body {
  background: transparent !important;
}

.race-analyzer-v2.light-layout .card-title,
.race-analyzer-v2.light-layout h6,
.race-analyzer-v2.light-layout h5 {
  color: #5e5873 !important;
}

.race-analyzer-v2.light-layout label {
  color: #5e5873 !important;
}

.race-analyzer-v2.light-layout .text-muted,
.race-analyzer-v2.light-layout small {
  color: #b9b9c3 !important;
}

.race-analyzer-v2.light-layout hr {
  border-top: 1px solid #ebe9f1 !important;
}

.race-analyzer-v2.light-layout .form-control {
  background: #fff !important;
  border: 1px solid #d8d6de !important;
  color: #5e5873 !important;
}

.race-analyzer-v2.light-layout .form-control:focus {
  background: #fff !important;
  border-color: #7367f0 !important;
  color: #5e5873 !important;
}

.race-analyzer-v2.light-layout .custom-select {
  background: #fff !important;
  border: 1px solid #d8d6de !important;
  color: #5e5873 !important;
}

.race-analyzer-v2.light-layout .custom-select:focus {
  background: #fff !important;
  border-color: #7367f0 !important;
}

.race-analyzer-v2.light-layout .custom-checkbox .custom-control-label {
  color: #5e5873 !important;
}

.race-analyzer-v2.light-layout .custom-checkbox .custom-control-input:checked ~ .custom-control-label::before {
  background-color: #7367f0 !important;
  border-color: #7367f0 !important;
}
</style>
